/**
 * App recipes (T9): the in-app acts a person asks for by voice at "open Slack" speed ("go to LD
 * in Slack", "open my DMs", "new tab", "open my chat with Priya in WhatsApp"). Jev picks one and
 * code runs it: the runner walks a recipe's steps in ONE server-to-runner dial
 * (os-recipe-handler.ts), with no model round.
 *
 * A recipe is a step list over sidecar ops that ALREADY exist (launch, the `interact` key chord,
 * `dictate` with no Return, the `apps` title read, the `selection` focus read, AppleScript and
 * URL verbs, contacts.search), so v1 ships in the runner bundle with no Swift. It is its own
 * table, never rows of OS_VERB_REGISTRY: the TS and Swift verb id sets must match, and a recipe
 * has no Swift body.
 *
 * What the table holds to, pinned in os-recipes.test.ts:
 *  - never an AX tree walk (no snapshot, find or click): Slack walks took 12-13 s, and
 *    snapshot/find re-point the ref table another session may be using;
 *  - no step presses a key after it types the person's words, and `type` never submits;
 *  - only app.send_draft sends, and it runs only on the person's own yes (send-draft-rung.ts);
 *  - a Slack name is typed only after the switcher took the focus, and a draft's words only
 *    after the front window's title named the target.
 *
 * Params: `span` is words cut from the person's own ask (a name, a tab title), `literal` is text
 * the literal gate said to type as it is, `enum` is one of the table's own values, `number` is
 * digits code parsed, `url` is an https address.
 */
import { LAUNCHABLE_APPS } from './os-verbs.js';

export type OsRecipeParamKind = 'span' | 'literal' | 'enum' | 'number' | 'url';

/**
 * The named rule that cuts a literal param out of the person's words (Mac recipes R4a; the server's
 * composer/recipe-cut.ts). A closed set: a rule that does not apply cuts nothing, never words of
 * our own, and none keeps the recipient's name, the app's name or a trailing "please".
 */
export type RecipeCut =
  /** The words after "saying", "that", a colon, or Hinglish "ki" (after the recipient's name when
   *  there is one, with only a kind word or an app between them; there "say" and "to say" too). */
  | 'after_saying'
  /** The words inside quotes. */
  | 'quoted'
  /** The words after "search", "google", "look up" or "look for" (T9's search rule), past a place
   *  that is this app or this page ("search my notes for kyoto"); Hinglish "notes mein X search karo". */
  | 'query'
  /** The words after "called", "named", "titled" or "call it". */
  | 'called'
  /** The words after the recipe's first cue, up to where they go: "remind me to call mom". */
  | 'to_end';

export interface OsRecipeParam {
  kind: OsRecipeParamKind;
  description: string;
  /** enum only: the values, and a name for each (Jev reads the name, the runner the value). */
  enum?: readonly string[];
  labels?: Readonly<Record<string, string>>;
  /** The one param that may be blank: the title a send expects (an app may have no title). */
  optional?: true;
  /** span: the words may be an app's name (a browser tab's title often is). Any other span that
   *  names an app is refused: a person or a channel is never "Discord". enum (R4b): its values are
   *  apps, and the label said names the app the act is for (the app to hide), never another app. */
  apps?: true;
  /** literal only: the rule that cuts it from their words, or its rules in order (the first that
   *  applies wins). Absent, T9's rule (a quote, the words after "saying" or the name; a param
   *  named `query` after "search"). */
  cut?: RecipeCut | readonly RecipeCut[];
}

export type OsRecipeTier = 'reversible' | 'unsent' | 'irreversible';
export type OsRecipeVerify = 'title' | 'contact' | 'echo';

/**
 * One step. `param` names a declared param; a template's `{slot}` is a param or the contact slot
 * the recipe's `contact` step resolved.
 *  - activate: launch or front the app (a cold one gets 5 s to show a titled window).
 *  - key: one chord through `interact` (the sidecar raises the app first).
 *  - type: `dictate` with submit false, never a Return.
 *  - focus_mark / focus_moved: read the focused element (`selection`: its role and label, one
 *    attribute read), then wait until the focus moved off the mark to an editable whose label
 *    starts with one of `labelIn` (a POSITIVE check: an empty or any other label refuses), and
 *    re-mark. Slack's switcher is known by its own label, never by what it is not.
 *  - verify_title: poll the front window's title until the conversation it names (its head) IS
 *    `param`, whole words, any case; never a match in the title's tail (the workspace).
 *  - contact: contacts.search to exactly ONE contact and ONE usable `want`, else ask.
 *  - open_url: system.open_url with a filled template. await_front: wait until the app is in front.
 *  - verb: one registry verb with filled params (a text slot as said, a URL slot encoded), each
 *    typed by the verb's own schema. pick_tab: one Chrome tab whose title holds the words.
 *  - memo: hand the server what a yes needs (the app, the title, the window).
 *  - send: app.send_draft's one act, re-reading the app and title before its key.
 *  - focus_not_editable: refuse while a text field has the focus (a bare-letter chord would type).
 *  - focus_is: wait until the focus is an editable whose label starts with one of `labelIn`.
 *  - verify_title_has: poll the front title until it holds the words, whole words, anywhere.
 *  - cycle_to_title: press `chord` until the title's head IS the words, at most `max` times.
 *  - menu: press a menu item through `interact`; `pair` is [this title, its opposite], so a miss
 *    can say the other state is likely already on.
 *  - resolve: match the words against a live list (a list verb's output); one match fills
 *    `into` (raw), two or more ask, none says so.
 *  - send_key: onYes only, the key that sends or commits, after the same re-check as `send`.
 */
export type OsRecipeStep =
  | { op: 'activate' }
  | { op: 'key'; chord: string }
  | { op: 'type'; param: string }
  | { op: 'settle'; ms: number }
  | { op: 'focus_mark' }
  | { op: 'focus_moved'; timeoutMs: number; labelIn: readonly string[] }
  | { op: 'verify_title'; param: string; timeoutMs: number }
  | { op: 'contact'; param: string; want: 'handle' | 'phone' | 'email' }
  /** `bundleId` needs the sidecar verb system.open_url_in (R1b); the lint gates it. */
  | { op: 'open_url'; template: string; bundleId?: string }
  | { op: 'await_front'; timeoutMs: number }
  | { op: 'verb'; id: string; params: Readonly<Record<string, string | number | boolean>> }
  | { op: 'pick_tab'; param: string }
  | { op: 'memo' }
  | { op: 'send' }
  /** Refuse if a text field has the focus. */
  | { op: 'focus_not_editable' }
  /** Wait until the focus is an editable whose label starts with one of labelIn. */
  | { op: 'focus_is'; labelIn: readonly string[]; timeoutMs: number }
  /** The front title holds the words (whole words), anywhere. */
  | { op: 'verify_title_has'; param: string; timeoutMs: number }
  /** Press chord until the title head is param, at most max times. */
  | { op: 'cycle_to_title'; chord: string; param: string; max: number }
  /** An interact menu press; pair = [this title, its opposite]. */
  | { op: 'menu'; path: readonly string[]; pair?: readonly [string, string] }
  /** Match param against a live list; unique fills values[into]. */
  | { op: 'resolve'; source: string; param: string; into: string }
  /** onYes only: the key that sends or commits. */
  | { op: 'send_key'; chord: string };

export interface OsRecipe {
  id: string;
  /** The app it acts in; null for app.send_draft (it acts in the draft's own app) and for a
   *  `system` recipe. */
  app: { bundleId: string; name: string } | null;
  /** No app of its own: a system act whose verbs name their own targets (volume, Do Not
   *  Disturb). Only verb steps; the floors apply per verb. */
  system?: true;
  title: string;
  description: string;
  params: Readonly<Record<string, OsRecipeParam>>;
  steps: readonly OsRecipeStep[];
  tier: OsRecipeTier;
  verify?: OsRecipeVerify;
  /** Words besides the app's own names that name it in an ask ("search google for ..."). */
  names?: readonly string[];
  /** Words one of which an ask must carry for Jev's pick of this recipe to stand: the act's own
   *  name ("unreads", "tab", "direct messages"). Code verifies what Jev selects, since Jev takes the
   *  NEAREST option: "open Slack" must never read as Slack's unreads. Whole words, any case. */
  cues: readonly string[];
  /** unsent only: the chord that sends the draft (Mail's compose Return is a new line). */
  sendKey?: string;
  /** The irreversible act (a send key, a destructive menu or verb): walked ONLY on a yes request
   *  (`phase: 'yes'`), after the runner re-checks the front window still names the target. */
  onYes?: readonly OsRecipeStep[];
  /** The one lower-case word the yes may say besides "yes": send, delete, call, share, archive,
   *  join, end, quit or move. "Send it" never answers a delete. Never a word that also says never
   *  mind (leave, stop, drop, skip, keep, hold, forget, cancel): the yes rung reads those as a no,
   *  so such a recipe takes only a plain yes. */
  yesWord?: string;
  /** Sidecar features or verb ids the recipe needs; offered only when the runner reports them. */
  requires?: readonly string[];
  /** Absent = live. A held recipe is never offered and never run. */
  status?: 'live' | 'held';
  /** R4b: the act opens or shows something (a place, a view, a pane, a dialog, a blank new thing),
   *  so a reach word ("open", "show", "go to", REACH_WORDS) is its glue ("open the parent folder").
   *  Absent, it acts, and a reach word is an ask it does not answer: "open the presentation" never
   *  plays it, "show the link" never copies it. Never on a toggle (it may close what "open" asked
   *  for) or an irreversible act (os-recipes-data.test.ts). Absent is the safe side: a missed opener
   *  goes to the model. */
  opens?: true;
  /** What the person hears when it lands: {label} is the target the runner resolved, and a
   *  param name is its value. An unsent recipe's reply is the question its yes answers. */
  reply: string;
}

/** The one recipe that sends. Never offered to Jev or the model: the yes rung alone runs it. */
export const OS_RECIPE_SEND_DRAFT = 'app.send_draft';

/** R4b: the act opens or shows something (`opens`), so a reach word is its glue. One source: the
 *  server's recipeFits and the data tests read this. */
export const recipeOpens = (r: OsRecipe): boolean => r.opens === true;

/** An opening recipe, as the helpers build it: `opening(menu(...))`. */
export const opening = (r: OsRecipe): OsRecipe => ({ ...r, opens: true });

/** R4b: a create act (its act opens new_, create_ or make_). */
export const recipeCreates = (r: OsRecipe): boolean => /^(?:new|create|make)(?:_|$)/.test(r.id.slice(r.id.indexOf('.') + 1));

/** R4b: the words that ask to REACH a thing (open it, show it, pull or bring it up, go to it, find,
 *  look or take me to it), in English and Hinglish: glue only for a recipe that opens (recipeOpens),
 *  read by the server's recipeFits and pinned by the data and corpus tests. */
export const REACH_WORDS: ReadonlySet<string> = new Set([
  'open', 'opens', 'khol', 'kholo', 'kholdo', 'show', 'pull', 'bring', 'launch', 'go', 'switch', 'fire', 'up',
  'chalao', 'chala', 'look', 'find', 'jump', 'take',
]);

/** Chrome's go-to sites, https only (the verb fast path's well-known sites, minus the app scheme). */
export const RECIPE_SITES: Readonly<Record<string, string>> = {
  YouTube: 'https://www.youtube.com',
  Gmail: 'https://mail.google.com',
  Google: 'https://www.google.com',
  GitHub: 'https://github.com',
  X: 'https://x.com',
  Instagram: 'https://www.instagram.com',
  Reddit: 'https://www.reddit.com',
  LinkedIn: 'https://www.linkedin.com',
  Notion: 'https://www.notion.so',
  Figma: 'https://www.figma.com',
  'Google Maps': 'https://maps.google.com',
  'Google Calendar': 'https://calendar.google.com',
  'Hacker News': 'https://news.ycombinator.com',
};

/** The deep-link schemes each app's recipes may open (https is always allowed). The lint allows a
 *  link only in these, and a template is a URL only when it opens with https or one of them. */
export const APP_URL_SCHEMES: Readonly<Record<string, readonly string[]>> = {
  'com.tinyspeck.slackmacgap': ['slack'],
  'notion.id': ['notion'],
  'com.linear': ['linear'],
  'com.figma.Desktop': ['figma'],
  'com.spotify.client': ['spotify'],
  'net.whatsapp.WhatsApp': ['whatsapp'],
  'com.apple.MobileSMS': ['imessage'],
  'com.apple.mail': ['mailto'],
  'com.apple.Photos': ['photos'],
  'com.apple.FaceTime': ['facetime', 'facetime-audio', 'tel'],
};

/** The schemes a recipe's app vouches for; none for an app-less or unlisted one. */
export function appUrlSchemes(app: OsRecipe['app']): readonly string[] {
  return app && Object.prototype.hasOwnProperty.call(APP_URL_SCHEMES, app.bundleId) ? APP_URL_SCHEMES[app.bundleId]! : [];
}

const URL_SCHEMES: ReadonlySet<string> = new Set(Object.values(APP_URL_SCHEMES).flat());

/** A launchable app as a recipe names it, or null: a bad id must never throw at import (it would
 *  take down every importer of @bash/shared, the server's boot included). */
export function recipeApp(bundleId: string): { bundleId: string; name: string } | null {
  const a = LAUNCHABLE_APPS.find((x) => x.bundleId === bundleId);
  return a ? { bundleId: a.bundleId, name: a.name } : null;
}
const app = (bundleId: string) => recipeApp(bundleId);
const SLACK = app('com.tinyspeck.slackmacgap');
const CHROME = app('com.google.Chrome');
const MESSAGES = app('com.apple.MobileSMS');
const WHATSAPP = app('net.whatsapp.WhatsApp');
const MAIL = app('com.apple.mail');
const CALENDAR = app('com.apple.iCal');
const NOTES = app('com.apple.Notes');

const NAME: OsRecipeParam = { kind: 'span', description: 'The person, channel or conversation to reach, in their words' };
const TEXT: OsRecipeParam = { kind: 'literal', description: 'The exact words of the message' };

/** The labels Slack's quick switcher field may carry (its placeholder), lower case, by prefix. Read
 *  from code, not yet from his Mac: the switcher recipes stay held (SLACK_RECIPES_LIVE) until a
 *  drill reads the real label through the `selection` op. */
export const SLACK_SWITCHER_LABELS: readonly string[] = ['search', 'jump to', 'go to'];

/**
 * The Slack recipes that type into the switcher (a conversation by name, and a draft) stay OFF
 * until the lead's drill reads Slack's real switcher and message-box labels: typed into any other
 * field, the Return after a name SENDS it. Slack's keys (DMs, unreads, back) type nothing and stay
 * live. Off: not offered to Jev, not the model's, refused by the runner.
 */
export const SLACK_RECIPES_LIVE = false;
const SLACK_SWITCHER_RECIPES: ReadonlySet<string> = new Set(['slack.open_conversation', 'slack.draft_message']);
let slackRecipesLive: boolean = SLACK_RECIPES_LIVE;

/** A recipe that may run today. */
export function isRecipeLive(id: string): boolean {
  return slackRecipesLive || !SLACK_SWITCHER_RECIPES.has(id);
}

/** Test-only: the switcher recipes' own tests (and an opt-in keyed eval) lift the hold. */
export function __setSlackRecipesLiveForTests(on: boolean): void {
  slackRecipesLive = on;
}

/** Slack's quick switcher, to a verified title. The name is typed only once the switcher holds
 *  the focus, known by its own label: pressed into a message box instead, the Return after it
 *  would SEND the name. */
const SLACK_SWITCH: readonly OsRecipeStep[] = [
  { op: 'activate' },
  { op: 'focus_mark' },
  { op: 'key', chord: 'cmd+k' },
  { op: 'focus_moved', timeoutMs: 800, labelIn: SLACK_SWITCHER_LABELS },
  { op: 'type', param: 'name' },
  { op: 'settle', ms: 350 },
  { op: 'key', chord: 'return' },
  // A beat for the switch to land, so the title from before the Return is never the one read.
  { op: 'settle', ms: 150 },
  { op: 'verify_title', param: 'name', timeoutMs: 1_500 },
];

/** A reversible chord in a running app. */
const chord = (id: string, a: OsRecipe['app'], title: string, description: string, key: string, cues: readonly string[], reply: string): OsRecipe => ({
  id, app: a, title, description, params: {}, steps: [{ op: 'activate' }, { op: 'key', chord: key }], tier: 'reversible', cues, reply,
});

/** The words that ask to reach a place, and the words that ask to write to someone. */
const GO: readonly string[] = ['go', 'open', 'switch', 'jump', 'take', 'show', 'pull', 'bring'];
const WRITE: readonly string[] = ['send', 'message', 'text', 'tell', 'dm', 'write', 'draft', 'reply', 'ping'];

/** Every recipe but the send, which reads this list for the drafts it may send. */
const ACTS: readonly OsRecipe[] = [
  {
    id: 'slack.open_conversation', app: SLACK, opens: true,
    title: 'Open a Slack conversation',
    description: 'Go to a person, DM or channel in the Slack app by name ("go to LD in Slack", "go to LD in DMs", "open the design channel on Slack").',
    // Its own Hinglish open words, never the shared GO ("LD ka DM kholo"). "dm" is never its cue: "dm LD
    // hi" is the draft's, and a name span could swallow the message. A DM's words are what its name
    // brings (recipe-words.ts NAME_BRINGS); the DMs list takes no name.
    params: { name: NAME }, steps: SLACK_SWITCH, tier: 'reversible', verify: 'title', cues: [...GO, 'kholo', 'khol', 'kholdo'],
    reply: 'Opened {label} in Slack.',
  },
  {
    id: 'slack.draft_message', app: SLACK,
    title: 'Write a Slack message without sending it',
    description: 'Open a Slack conversation and write a message there, unsent, for them to approve ("send LD I\'ll be late", "message the design channel saying the build is up").',
    params: { name: NAME, text: TEXT },
    steps: [...SLACK_SWITCH, { op: 'focus_moved', timeoutMs: 800, labelIn: ['message'] }, { op: 'type', param: 'text' }, { op: 'memo' }],
    tier: 'unsent', verify: 'title', sendKey: 'return', cues: WRITE,
    reply: "It's written to {label} in Slack. Send it?",
  },
  opening(chord('slack.unreads', SLACK, 'Show Slack unreads', 'Show all unread messages in the Slack app ("show my unreads in Slack").', 'cmd+shift+a', ['unread', 'unreads'], 'Opened your unreads in Slack.')),
  opening(chord('slack.dms', SLACK, 'Show Slack DMs', 'Show the list of direct messages in the Slack app, no one named ("open my DMs").', 'cmd+shift+k', ['dm', 'dms', 'direct messages', 'direct message'], 'Opened your DMs in Slack.')),
  opening(chord('slack.back', SLACK, 'Go back in Slack', 'Go back to the previous conversation in the Slack app ("go back in Slack").', 'cmd+[', ['back', 'previous', 'conversation'], 'Went back in Slack.')),
  // R4b: a create act is cued by its create word, never the bare thing (os-recipes-data.test.ts): "go to
  // the gmail tab" is never a blank tab, "open my groceries note" never a blank note.
  opening(chord('chrome.new_tab', CHROME, 'Open a new Chrome tab', 'Open a new empty tab in Google Chrome ("new tab").', 'cmd+t', ['new tab', 'new chrome tab', 'naya tab', 'another tab', 'blank tab', 'fresh tab'], 'Opened a new tab in Chrome.')),
  {
    id: 'chrome.go_to', app: CHROME, opens: true,
    title: 'Open a well-known site in Chrome',
    description: 'Open a well-known site in a new Google Chrome tab ("open YouTube in Chrome").',
    params: { site: { kind: 'enum', description: 'The site to open', enum: Object.values(RECIPE_SITES), labels: Object.fromEntries(Object.entries(RECIPE_SITES).map(([n, u]) => [u, n])) } },
    steps: [{ op: 'activate' }, { op: 'verb', id: 'chrome.open_tab', params: { url: '{site}' } }],
    tier: 'reversible', verify: 'echo', cues: [...GO, 'visit', 'load'], reply: 'Opened {label} in Chrome.',
  },
  {
    id: 'chrome.search', app: CHROME, opens: true,
    title: 'Search Google in Chrome',
    description: 'Search Google for their words in a new Google Chrome tab ("search Google for kimi k3").',
    params: { query: { kind: 'literal', description: 'The exact words to search for' } },
    steps: [{ op: 'activate' }, { op: 'verb', id: 'chrome.open_tab', params: { url: 'https://www.google.com/search?q={query}' } }],
    tier: 'reversible', verify: 'echo', names: ['google'], cues: ['search', 'google', 'look'], reply: 'Searched Google for {query}.',
  },
  {
    id: 'chrome.switch_tab', app: CHROME, opens: true,
    title: 'Switch to a Chrome tab',
    description: 'Switch to the one open Google Chrome tab whose title they name ("switch to the Figma tab in Chrome").',
    params: { title: { kind: 'span', description: 'Words from the title of the tab they want', apps: true } },
    steps: [{ op: 'activate' }, { op: 'pick_tab', param: 'title' }],
    tier: 'reversible', verify: 'echo', cues: ['tab', 'switch'], reply: 'Switched to {label} in Chrome.',
  },
  {
    id: 'messages.open_conversation', app: MESSAGES, opens: true,
    title: 'Open a Messages chat',
    description: 'Open the Messages chat with one of their contacts ("open my chat with Priya in Messages").',
    params: { name: NAME },
    steps: [{ op: 'contact', param: 'name', want: 'handle' }, { op: 'open_url', template: 'imessage:{handle}' }, { op: 'await_front', timeoutMs: 3_000 }],
    tier: 'reversible', verify: 'contact', cues: [...GO, 'chat', 'conversation', 'thread'], reply: 'Opened your chat with {label} in Messages.',
  },
  {
    id: 'messages.draft', app: MESSAGES,
    title: 'Write an iMessage without sending it',
    description: 'Open the Messages chat with one of their contacts and write a message there, unsent ("text Priya on iMessage saying I\'m on my way").',
    params: { name: NAME, text: TEXT },
    steps: [
      { op: 'contact', param: 'name', want: 'handle' }, { op: 'open_url', template: 'imessage:{handle}' },
      { op: 'await_front', timeoutMs: 3_000 }, { op: 'settle', ms: 400 }, { op: 'type', param: 'text' }, { op: 'memo' },
    ],
    tier: 'unsent', verify: 'contact', sendKey: 'return', cues: [...WRITE, 'imessage'], reply: "It's written to {label} in Messages. Send it?",
  },
  {
    id: 'whatsapp.open_chat', app: WHATSAPP, opens: true,
    title: 'Open a WhatsApp chat',
    description: 'Open the WhatsApp chat with one of their contacts ("open my chat with Priya in WhatsApp").',
    params: { name: NAME },
    steps: [{ op: 'contact', param: 'name', want: 'phone' }, { op: 'open_url', template: 'whatsapp://send?phone={phone}' }, { op: 'await_front', timeoutMs: 3_000 }],
    tier: 'reversible', verify: 'contact', cues: [...GO, 'chat', 'conversation'], reply: 'Opened your chat with {label} in WhatsApp.',
  },
  {
    id: 'whatsapp.draft', app: WHATSAPP,
    title: 'Write a WhatsApp message without sending it',
    description: 'Open the WhatsApp chat with one of their contacts with a message written in it, unsent ("WhatsApp Priya that I\'m running late").',
    params: { name: NAME, text: TEXT },
    steps: [
      { op: 'contact', param: 'name', want: 'phone' }, { op: 'open_url', template: 'whatsapp://send?phone={phone}&text={text}' },
      { op: 'await_front', timeoutMs: 3_000 }, { op: 'memo' },
    ],
    tier: 'unsent', verify: 'contact', sendKey: 'return', cues: [...WRITE, 'whatsapp'], reply: "It's written to {label} in WhatsApp. Send it?",
  },
  opening(chord('mail.new_message', MAIL, 'New email', 'Start a new blank email in the Mail app ("new email in Mail").', 'cmd+n', ['compose', 'compose email', 'compose an email', 'new email', 'new mail', 'new message', 'new mail message', 'naya email', 'blank email', 'another email'], 'Started a new email in Mail.')),
  {
    id: 'mail.draft_to', app: MAIL,
    title: 'Write an email without sending it',
    description: 'Start an email in Mail to one of their contacts with their words in it, unsent ("email Priya saying the deck is ready").',
    params: { name: NAME, text: TEXT },
    steps: [
      { op: 'contact', param: 'name', want: 'email' }, { op: 'open_url', template: 'mailto:{email}?body={text}' },
      { op: 'await_front', timeoutMs: 3_000 }, { op: 'memo' },
    ],
    tier: 'unsent', verify: 'contact', sendKey: 'cmd+shift+d', cues: [...WRITE, 'email', 'mail'], reply: "It's written to {label} in Mail. Send it?",
  },
  opening(chord('calendar.today', CALENDAR, 'Show today in Calendar', 'Jump the Calendar app to today ("show today in my calendar").', 'cmd+t', ['today'], 'Showing today in Calendar.')),
  opening(chord('notes.new_note', NOTES, 'New note', 'Start a new blank note in the Notes app ("new note").', 'cmd+n', ['new note', 'naya note', 'note banao', 'naya note banao', 'another note', 'blank note', 'fresh note', 'take a note', 'make a note', 'create a note'], 'Started a new note in Notes.')),
];

const SEND_DRAFT: OsRecipe = {
  id: OS_RECIPE_SEND_DRAFT, app: null,
  title: 'Send the written draft',
  description: 'Send the message a draft recipe wrote, in the same window, on their own yes.',
  params: {
    draft: { kind: 'enum', description: 'The draft recipe that wrote it', enum: ACTS.filter((r) => r.tier === 'unsent').map((r) => r.id) },
    title: { kind: 'literal', description: 'The front window title the draft left', optional: true },
  },
  steps: [{ op: 'send' }],
  tier: 'irreversible', verify: 'title', cues: [], reply: 'Sent.',
};

/** Keep the recipes that can run; say why each other one cannot. Never throws. */
export function defineRecipes(list: readonly (OsRecipe | null)[]): { recipes: OsRecipe[]; dropped: string[] } {
  const recipes: OsRecipe[] = [];
  const dropped: string[] = [];
  for (const r of list) {
    if (!r) { dropped.push('(null): its app is not launchable'); continue; }
    if (r.app === null && r.system !== true && r.id !== OS_RECIPE_SEND_DRAFT) { dropped.push(`${r.id}: no app to act in`); continue; }
    recipes.push(r);
  }
  return { recipes, dropped };
}

const defined = defineRecipes([...ACTS, SEND_DRAFT]);
export const OS_RECIPES: readonly OsRecipe[] = defined.recipes;
/** The recipes the table could not keep, each with its reason. A test pins this empty. */
export const OS_RECIPES_DROPPED: readonly string[] = defined.dropped;

const byId = new Map<string, OsRecipe>(OS_RECIPES.map((r) => [r.id, r]));

export function getOsRecipe(id: string): OsRecipe | undefined {
  return byId.get(id);
}

/** A name or a tab title; a message or a search. Longer is not a name. */
const MAX_SPAN_CHARS = 80;
const MAX_LITERAL_CHARS = 2_000;

/** The params of one run, field by field: every declared param (all are required but the one
 *  `optional`), trimmed, bounded, an enum's value from its own list; nothing undeclared. */
export function validateRecipeParams(
  recipe: OsRecipe,
  params: unknown,
): { ok: true; params: Record<string, string> } | { ok: false; error: string } {
  const given = params === undefined ? {} : params;
  if (typeof given !== 'object' || given === null || Array.isArray(given)) return { ok: false, error: 'params must be an object' };
  const raw = given as Record<string, unknown>;
  for (const key of Object.keys(raw)) if (!recipe.params[key]) return { ok: false, error: `unknown param ${key}` };
  const out: Record<string, string> = {};
  for (const [name, p] of Object.entries(recipe.params)) {
    const v = raw[name];
    if (typeof v !== 'string') {
      if (v === undefined && p.optional) { out[name] = ''; continue; }
      return { ok: false, error: `${name} must be text` };
    }
    const value = v.trim();
    if (!value && !p.optional) return { ok: false, error: `${name} is blank` };
    if (p.kind === 'enum' && !(p.enum ?? []).includes(value)) return { ok: false, error: `${name} is not one of its values` };
    if (p.kind === 'span' && value.length > MAX_SPAN_CHARS) return { ok: false, error: `${name} is too long` };
    if (p.kind === 'literal' && value.length > MAX_LITERAL_CHARS) return { ok: false, error: `${name} is too long` };
    // A newline typed by dictate is a Return, and a Return sends: the person's words never carry one.
    if ((p.kind === 'span' || p.kind === 'literal') && /\p{Cc}/u.test(value)) return { ok: false, error: `${name} has a control character` };
    if (p.kind === 'number' && !/^-?\d{1,9}(?:\.\d{1,3})?$/.test(value)) return { ok: false, error: `${name} is not a number` };
    if (p.kind === 'url' && !/^https:\/\/[^\s]{1,2000}$/.test(value)) return { ok: false, error: `${name} is not a web address` };
    out[name] = value;
  }
  return { ok: true, params: out };
}

/** The `{slot}` names of a template, in order. */
export function recipeTemplateSlots(template: string): string[] {
  return [...template.matchAll(/\{([a-z_]+)\}/g)].map((m) => m[1]!);
}

/** A template is a URL only when it opens with https:// or an app's own deep-link scheme
 *  (APP_URL_SCHEMES): the person's words go in encoded. Anything else is text, as said ("Re: {text}"
 *  holds a colon and is still words). One rule, for the lint and the runner. */
export function isUrlTemplate(t: string): boolean {
  if (t.slice(0, 8).toLowerCase() === 'https://') return true;
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(t)?.[1]?.toLowerCase();
  return scheme !== undefined && URL_SCHEMES.has(scheme);
}

/**
 * Fill a template. In a URL (`mode` 'url', the default) the person's words are URL-encoded; in
 * text ('text', a verb param that is not a URL) they go in as they said them. A slot in `raw` (a
 * table value, or a contact value the runner already held to a safe shape) goes in as it is. A
 * missing slot fills as blank, which the caller's own validation then refuses.
 */
export function fillRecipeTemplate(template: string, values: Readonly<Record<string, string>>, raw: ReadonlySet<string> = new Set(), mode: 'url' | 'text' = 'url'): string {
  return template.replace(/\{([a-z_]+)\}/g, (_, slot: string) => {
    const v = values[slot] ?? '';
    return mode === 'text' || raw.has(slot) ? v : encodeURIComponent(v);
  });
}

/** Links whose address (who they open to) takes a contact's held value as it is: a FaceTime,
 *  iMessage or phone handle. Anywhere else a contact's value is encoded like any words. */
export const LINK_ADDRESS_SCHEMES: readonly string[] = ['facetime', 'facetime-audio', 'imessage', 'tel'];

/** Where a link's address sits: its authority after "//", or (for a FaceTime, iMessage or phone
 *  link) its text before "?". The host of any "//" link; null for an opaque link of another scheme. */
export function linkAddress(template: string): { start: number; end: number } | null {
  const m = /^([a-z][a-z0-9+.-]*):(\/\/)?/i.exec(template);
  if (!m) return null;
  const start = m[0].length;
  if (!m[2] && !LINK_ADDRESS_SCHEMES.includes(m[1]!.toLowerCase())) return null;
  const rest = template.slice(start).search(m[2] ? /[/?#]/ : /[?#]/);
  return { start, end: rest < 0 ? template.length : start + rest };
}

/** Fill a link: its address takes `addressRaw` slots (a contact's held handle) as they are, but
 *  only for a FaceTime, iMessage or phone link; every other slot is encoded unless it is in `raw`
 *  (a table value). */
export function fillRecipeLink(template: string, values: Readonly<Record<string, string>>, raw: ReadonlySet<string>, addressRaw: ReadonlySet<string>): string {
  const at = linkAddress(template);
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(template)?.[1]?.toLowerCase() ?? '';
  if (!at || !LINK_ADDRESS_SCHEMES.includes(scheme)) return fillRecipeTemplate(template, values, raw, 'url');
  const both = new Set([...raw, ...addressRaw]);
  return fillRecipeTemplate(template.slice(0, at.start), values, raw, 'url') +
    fillRecipeTemplate(template.slice(at.start, at.end), values, both, 'url') +
    fillRecipeTemplate(template.slice(at.end), values, raw, 'url');
}

/** A window title as words: split on spaces and the separators apps put between parts, but
 *  never inside a name ("ld-bots", "q3.plan", "o'brien" stay one word). */
const titleWords = (s: string): string[] =>
  s.toLowerCase().split(/[\s()[\]{}|,:;·•–—#@"“”]+/u).filter((w) => w !== '' && w !== '-');

/**
 * True when the title names the target: the name's words appear in the title as whole words, in
 * order. "LD" names "LD (DM) - Acme - Slack" and never "ld-bots (Channel) - Acme - Slack".
 */
export function titleNames(title: string, name: string): boolean {
  const want = titleWords(name);
  const have = titleWords(title);
  if (want.length === 0 || have.length < want.length) return false;
  for (let i = 0; i + want.length <= have.length; i++) {
    if (want.every((w, j) => have[i + j] === w)) return true;
  }
  return false;
}

/**
 * True when the conversation the title names (titleHead) IS the name, on whole words, any case:
 * "LD" is "LD (DM) - Acme - Slack", never "LD Team (Channel) - ...", and "Acme" is never the
 * workspace in a title's tail. What the switcher verifies against before a draft types a word.
 */
export function titleHeadIs(title: string, name: string): boolean {
  const want = titleWords(name);
  const head = titleWords(titleHead(title));
  return want.length > 0 && want.length === head.length && want.every((w, i) => head[i] === w);
}

/** The conversation a title names, for the reply: its first part, without a trailing "(DM)". */
export function titleHead(title: string): string {
  const first = title.split(/\s+[-|–—]\s+/u)[0] ?? '';
  return first.replace(/\s*\([^)]*\)\s*$/u, '').trim();
}
